
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import {
  Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Menu, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  logout, logoutAll, updatePassword, updateProfile,
} from "../../services/AuthenticationService";
import { useAuthStore } from "../../stores/authStore";
import { getApiErrorMessage } from "../../utils/apiData";

const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 38, borderRadius: 0, fontSize: "0.72rem" },
  "& .MuiInputLabel-root": { fontSize: "0.72rem" },
};

function SquareDialog({ open, onClose, title, children, actions }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 850, borderBottom: "1px solid #E3E7EC" }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: 2, bgcolor: "#FBFCFD" }}>{children}</DialogContent>
      <DialogActions sx={{ borderTop: "1px solid #E3E7EC" }}>{actions}</DialogActions>
    </Dialog>
  );
}

export function AccountMenu() {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const signOut = useAuthStore((s) => s.signOut);

  const [anchor, setAnchor] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [message, setMessage] = useState(null);
  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "" });
  const [password, setPassword] = useState({
    current_password: "", password: "", password_confirmation: "",
  });

  const initials = useMemo(
    () => String(user?.name || "SC").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase(),
    [user?.name],
  );

  const profileMutation = useMutation({
    mutationFn: () => updateProfile(profile),
    onSuccess: (response) => {
      const updated = response?.user ?? response?.data?.user ?? response?.data;
      if (updated) updateUser(updated);
      setDialog(null);
      setMessage(response?.message || "Profile updated successfully.");
    },
    onError: (error) => setMessage(getApiErrorMessage(error)),
  });

  const passwordMutation = useMutation({
    mutationFn: () => updatePassword(password),
    onSuccess: (response) => {
      setDialog(null);
      setPassword({ current_password: "", password: "", password_confirmation: "" });
      setMessage(response?.message || "Password updated successfully.");
    },
    onError: (error) => setMessage(getApiErrorMessage(error)),
  });

  const doLogout = async (all = false) => {
    try {
      if (all) await logoutAll();
      else await logout();
    } finally {
      signOut();
      window.location.replace("/login");
    }
  };

  return (
    <>
      <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ p: 0 }}>
        <Avatar variant="square" sx={{ width: 36, height: 36, bgcolor: "#3D5AFE", fontSize: "0.72rem", fontWeight: 800 }}>
          {initials}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 230, borderRadius: 0, border: "1px solid #E3E7EC" } }}
      >
        <Box sx={{ px: 1.5, py: 1.2 }}>
          <Typography sx={{ fontSize: "0.74rem", fontWeight: 800 }}>{user?.name}</Typography>
          <Typography sx={{ color: "#667085", fontSize: "0.64rem" }}>{user?.email}</Typography>
        </Box>
        <MenuItem onClick={() => { setAnchor(null); setDialog("profile"); }} sx={{ gap: 1, fontSize: "0.7rem" }}>
          <ManageAccountsOutlinedIcon sx={{ fontSize: 16 }} /> Update profile
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); setDialog("password"); }} sx={{ gap: 1, fontSize: "0.7rem" }}>
          <LockResetOutlinedIcon sx={{ fontSize: 16 }} /> Change password
        </MenuItem>
        <MenuItem onClick={() => doLogout(false)} sx={{ gap: 1, fontSize: "0.7rem" }}>
          <LogoutOutlinedIcon sx={{ fontSize: 16 }} /> Logout
        </MenuItem>
        <MenuItem onClick={() => doLogout(true)} sx={{ gap: 1, color: "error.main", fontSize: "0.7rem" }}>
          <PowerSettingsNewOutlinedIcon sx={{ fontSize: 16 }} /> Logout all devices
        </MenuItem>
      </Menu>

      <SquareDialog
        open={dialog === "profile"}
        onClose={() => setDialog(null)}
        title="Update profile"
        actions={
          <>
            <Button onClick={() => setDialog(null)} color="inherit" sx={{ borderRadius: 0 }}>Cancel</Button>
            <Button onClick={() => profileMutation.mutate()} variant="contained" disabled={profileMutation.isPending} sx={{ borderRadius: 0 }}>
              {profileMutation.isPending ? "Saving..." : "Save profile"}
            </Button>
          </>
        }
      >
        <Stack spacing={1.2}>
          <TextField label="Full name" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} sx={fieldSx} />
          <TextField label="Email address" value={profile.email} onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} sx={fieldSx} />
        </Stack>
      </SquareDialog>

      <SquareDialog
        open={dialog === "password"}
        onClose={() => setDialog(null)}
        title="Change password"
        actions={
          <>
            <Button onClick={() => setDialog(null)} color="inherit" sx={{ borderRadius: 0 }}>Cancel</Button>
            <Button onClick={() => passwordMutation.mutate()} variant="contained" disabled={passwordMutation.isPending} sx={{ borderRadius: 0 }}>
              {passwordMutation.isPending ? "Updating..." : "Update password"}
            </Button>
          </>
        }
      >
        <Stack spacing={1.2}>
          <TextField label="Current password" type="password" value={password.current_password} onChange={(e) => setPassword((p) => ({ ...p, current_password: e.target.value }))} sx={fieldSx} />
          <TextField label="New password" type="password" value={password.password} onChange={(e) => setPassword((p) => ({ ...p, password: e.target.value }))} sx={fieldSx} />
          <TextField label="Confirm new password" type="password" value={password.password_confirmation} onChange={(e) => setPassword((p) => ({ ...p, password_confirmation: e.target.value }))} sx={fieldSx} />
        </Stack>
      </SquareDialog>

      <Dialog open={Boolean(message)} onClose={() => setMessage(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontSize: "0.82rem", fontWeight: 850 }}>Account</DialogTitle>
        <DialogContent dividers><Typography sx={{ fontSize: "0.7rem" }}>{message}</Typography></DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setMessage(null)} sx={{ borderRadius: 0 }}>OK</Button></DialogActions>
      </Dialog>
    </>
  );
}
